/* Части формы «Сменить голос в записи» / «Почистить запись» по разметке прототипа (assets/bot-voice.js, BotCore.upload, BotCore.wave):
   волна, рамка загрузки звука (пусто · запись с микрофона · файл), «Прослушать запись» под рамкой и строка «Голос ›» (.voicepill). */
import { forwardRef, useMemo } from 'react';
import type { Voice } from '../../api/types';
import { voiceOrb } from '../../bridge/voice-orb';
import { uiText } from '../../i18n';
import { StudioIcon } from '../studio/StudioIcon';
import { ToolIcon } from '../studio/circle/ToolIcon';
import { fsize, mmss, voiceSub } from './voice-model';

/** Волна BotCore.wave.html: тихий слой b0 и яркий b1 (открывается до --p у родителя) */
export function Wave({ peaks }: { peaks: number[] }) {
  const lines = useMemo(
    () =>
      peaks.map((peak, index) => {
        const h = Math.max(1, peak * 16),
          x = index * 4 + 2;
        return <line key={index} x1={x} x2={x} y1={(18 - h).toFixed(1)} y2={(18 + h).toFixed(1)} />;
      }),
    [peaks],
  );
  const width = Math.max(4, peaks.length * 4);
  return (
    <>
      <svg className="b0" viewBox={`0 0 ${width} 36`} preserveAspectRatio="none" aria-hidden="true">
        {lines}
      </svg>
      <svg className="b1" viewBox={`0 0 ${width} 36`} preserveAspectRatio="none" aria-hidden="true">
        {lines}
      </svg>
    </>
  );
}

export interface VoiceUploadProps {
  /** Запись в рамке: имя, размер, длительность и волна (пока не прочитаны — null) */
  file: { name: string; size: number; durationMs: number | null; peaks: number[] | null; recorded: boolean } | null;
  title: string;
  sub: string;
  note: string;
  /** Подпись-ошибка вместо строки ограничений («Длиннее 5:00, загрузите короче»); рамка дрожит */
  say: string;
  accept: string[];
  disabled: boolean;
  /** Идёт запись с микрофона: requesting | recording, время и предел */
  rec: { state: 'idle' | 'requesting' | 'recording'; elapsedMs: number; maxMs: number };
  /** Доля прослушивания 0…1 (яркая часть волны), null — не слушают */
  progress: number | null;
  onPick(file: File): void;
  onRecord(): void;
  onRecordStop(): void;
  onRecordCancel(): void;
  onRemove(): void;
}
/** Рамка загрузки звука (.bcu, id vcUpl): пусто — «Загрузите запись», «Загрузить · Записать», ограничения; с записью — волна, имя,
    «3:12 · 4,4 МБ» и «Заменить · Убрать»; запись с микрофона — плитка .ocard.rec в той же рамке */
export const VoiceUpload = forwardRef<HTMLDivElement, VoiceUploadProps>(function VoiceUpload(props, ref) {
  const { file, rec } = props;
  const live = rec.state !== 'idle';
  const kind = live ? 'live' : file ? 'file' : 'empty';
  const pickInput = (label: string, cls: string, icon: string, text: string) => (
    <label className={`bcu-pl${cls ? ' ' + cls : ''}`}>
      <ToolIcon kind={icon} />
      <span>{uiText(text)}</span>
      <input
        type="file"
        className="studio-native-file-input"
        aria-label={uiText(label)}
        accept={props.accept.join(',')}
        disabled={props.disabled}
        hidden
        onChange={event => {
          const next = event.target.files?.[0];
          event.target.value = '';
          if (next) props.onPick(next);
        }}
      />
    </label>
  );
  const playing = props.progress !== null;
  return (
    <div
      ref={ref}
      id="vcUpl"
      className={`bcu${kind !== 'empty' ? ' has' : ''}${playing ? ' vc-play' : ''}`}
      data-bytes={file?.size ?? ''}
      style={{ ['--p' as string]: playing ? (Math.min(1, props.progress!) * 100).toFixed(2) : '0' }}
    >
      <div className="bcu-st" data-k={kind}>
        {kind === 'live' && (
          <div className="bcu-live ap-src2">
            <div className={`ocard rec${rec.state === 'recording' ? ' on' : ''}`} id="vcRecTile">
              <button
                className="rb"
                type="button"
                aria-label={uiText(rec.state === 'recording' ? 'Остановить запись' : 'Запрашиваем микрофон')}
                onClick={props.onRecordStop}
              >
                <i />
              </button>
              <span className="wavebar live dwv" aria-hidden="true">
                {Array.from({ length: 32 }, (_, index) => (
                  <i key={index} />
                ))}
              </span>
              <span className="rt">
                <b>{mmss(rec.elapsedMs / 1000)}</b> · {uiText('до')} {mmss(rec.maxMs / 1000)}
              </span>
              <button className="x" type="button" aria-label={uiText('Отменить запись')} onClick={props.onRecordCancel}>
                <StudioIcon kind="close" />
              </button>
            </div>
          </div>
        )}
        {kind === 'empty' && (
          <div className="bcu-e">
            <span className="bcu-ic">
              <ToolIcon kind="upload" />
            </span>
            <b className="bcu-t">{uiText(props.title)}</b>
            <span className="bcu-s">{uiText(props.sub)}</span>
            <div className="bcu-acts">
              {pickInput('Загрузить запись', 'pri', 'upload', 'Загрузить')}
              <button className="bcu-pl" type="button" disabled={props.disabled} onClick={props.onRecord}>
                <StudioIcon kind="mic" />
                <span>{uiText('Записать')}</span>
              </button>
            </div>
            <small className={`bcu-n${props.say ? ' bad' : ''}`} id="vcUplN" role={props.say ? 'alert' : undefined}>
              {uiText(props.say || props.note)}
            </small>
          </div>
        )}
        {kind === 'file' && file && (
          <div className="bcu-f">
            <div className="bcu-au">
              <span className="bcu-wv" aria-hidden="true">
                {file.peaks && <Wave peaks={file.peaks} />}
              </span>
              <span className="bcu-am">
                <b>{file.recorded ? uiText('Запись с микрофона') : file.name}</b>
                <span className="mono">
                  {file.durationMs ? `${mmss(file.durationMs / 1000)} · ` : ''}
                  {fsize(file.size)}
                </span>
              </span>
            </div>
            <div className="bcu-fa">
              {pickInput('Заменить запись', 'gl', 'refresh', 'Заменить')}
              <button className="bcu-pl gl" type="button" disabled={props.disabled} onClick={props.onRemove}>
                <ToolIcon kind="trash" />
                <span>{uiText('Убрать')}</span>
              </button>
            </div>
          </div>
        )}
      </div>
      {kind === 'file' && props.say && (
        <small className="bcu-n bad vc-say" role="alert">
          {uiText(props.say)}
        </small>
      )}
    </div>
  );
});

/** «Прослушать запись» под рамкой: кнопка, подпись и имя, время («0:41 / 3:12» по ходу) */
export function VoiceListen({
  name,
  recorded,
  durationMs,
  positionMs,
  playing,
  onToggle,
}: {
  name: string;
  recorded: boolean;
  durationMs: number;
  positionMs: number;
  playing: boolean;
  onToggle(): void;
}) {
  const active = playing || positionMs > 0;
  return (
    <div className={`vc-ls${playing ? ' on' : ''}`} id="vcLs">
      <button className="pb" type="button" aria-label={uiText(playing ? 'Пауза' : 'Прослушать')} onClick={onToggle}>
        <ToolIcon kind={playing ? 'pause' : 'play'} />
      </button>
      <span className="m">
        <b>{uiText(playing ? 'Звучит запись' : 'Прослушать запись')}</b>
        <span>{recorded ? uiText('Запись с микрофона') : name}</span>
      </span>
      <span className="mono" id="vcFd">
        {active ? `${mmss(positionMs / 1000)} / ${mmss(durationMs / 1000)}` : mmss(durationMs / 1000)}
      </span>
    </div>
  );
}

/** Строка «Голос ›» (.voicepill прототипа): шар голоса, «Голос: имя», подпись; не выбран — «Выберите голос · Библиотека и ваши клоны»;
    голос без переозвучки — подпись красным «Без переозвучки, выберите другой» */
export function VoiceLine({
  voice,
  total,
  disabled,
  busy,
  onOpen,
}: {
  voice: Voice | null;
  total?: number | null;
  disabled: boolean;
  busy: boolean;
  onOpen(): void;
}) {
  const bad = !!voice && !voice.is_s2s;
  const orb = voice ? voiceOrb(voice) : null;
  return (
    <div className="vc-vpw" id="vcVpW">
      <button
        className={`voicepill vc-vp${bad ? ' bad' : ''}`}
        type="button"
        id="vcVp"
        disabled={disabled || busy}
        aria-busy={busy}
        onClick={onOpen}
      >
        <span
          className={`orb${voice ? '' : ' no'}`}
          style={orb ? { background: orb } : undefined}
        >
          {!voice && <StudioIcon kind="wave" />}
        </span>
        <span className="m">
          <b>{voice ? `${uiText('Голос')}: ${voice.name}` : uiText('Выберите голос')}</b>
          <span>{uiText(voiceSub(voice, total))}</span>
        </span>
        <span className="ch">
          <StudioIcon kind="chev" />
        </span>
      </button>
    </div>
  );
}
